import { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { GetAgeInYears } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { SLICES } from '../../../constants/slices';
import { useGetPetInfoQuery } from '../../../data/policy';
import { getAgeInDays } from '../../../helpers/petHelpers';
import { BrandContext } from '../../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import {
  CHAT_TYPE,
  GENDER_VALUE,
  MALE_GENDER,
  DOG_SPECIES_CODE,
  CAT_SPECIES,
  DOG_SPECIES
} from '../../../constants/constants';
import { SESSION_STORAGE_KEYS } from '../../../constants/sessionStorageKeys';
import { FORM_KEYS } from '../../../constants/formKeys';
import { useGenerateConsultationUrlMutation } from '../../../data/liveVet';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { getGtmService } from '../../../services/gtmService/GtmService';

export const useLiveVetForm = (setConsultationUrl) => {
  const [generateConsultationUrl] = useGenerateConsultationUrlMutation();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const skipQueryOnLiteMode = isLiteModeEnabled !== false;
  const { cspMfaUrl } =
    useContext(BrandContext).activeBrandContext.cspConfiguration.auth0Config[process.env.REACT_APP_ENV];
  const {
    activeBrandContext: {
      brand: { partnerPrefix },
      cspConfiguration: {
        liveVetConfig: {
          [process.env.REACT_APP_ENV]: { vetChatAppModule, vetChatConsultationUrl }
        }
      }
    }
  } = useContext(BrandContext);

  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken || skipQueryOnLiteMode
  });
  const [failedToStartConsultation, setFailedToStartConsultation] = useState(false);
  const [selectedPolicyDetails, setSelectedPolicyDetails] = useState(null);
  const formContext = useForm({
    mode: 'onChange',
    defaultValues: {
      chatType: CHAT_TYPE.VIDEO,
      petGender: GENDER_VALUE.MALE_GENDER_VALUE,
      fullPolicyNumber: ''
    }
  });

  const chatType = formContext.watch(FORM_KEYS.CHAT_TYPE);
  const petGender = formContext.watch(FORM_KEYS.PET_GENDER);
  const fullPolicyNumber = formContext.watch(FORM_KEYS.FULL_POLICY_NUMBER);
  const { reset, handleSubmit, getValues, setValue } = formContext;

  useEffect(() => {
    if (isLiteModeEnabled) {
      reset();
    }
  }, [isLiteModeEnabled, reset]);

  useEffect(() => {
    if (petDetails && !petDetailsLoading && !isLiteModeEnabled) {
      const initialSelectedPolicy = { ...petDetails[0] };
      const ageInDays = getAgeInDays(new Date(initialSelectedPolicy.DateOfBirth));

      const initPetGenderValue =
        initialSelectedPolicy.PolicyDetails.PetGender === MALE_GENDER
          ? GENDER_VALUE.MALE_GENDER_VALUE
          : GENDER_VALUE.FEMALE_GENDER_VALUE;

      // Apply pet details on the form field
      setValue(FORM_KEYS.FULL_POLICY_NUMBER, initialSelectedPolicy.FullPolicyNumber);
      setValue(FORM_KEYS.PET_GENDER, initPetGenderValue);
      setValue(FORM_KEYS.PET_NAME, initialSelectedPolicy.Name);
      setValue(FORM_KEYS.PET_SPECIES, initialSelectedPolicy.Species);
      setValue(FORM_KEYS.PET_BREED, initialSelectedPolicy.Breed);
      setValue(FORM_KEYS.PET_AGE, GetAgeInYears({ ageInDays }) || 0);
      setValue(FORM_KEYS.FIRST_NAME, initialSelectedPolicy?.PolicyDetails?.PolicyHolder?.firstName);
      setValue(FORM_KEYS.LAST_NAME, initialSelectedPolicy?.PolicyDetails?.PolicyHolder?.lastName);
      setValue(FORM_KEYS.EMAIL_ADDRESS, initialSelectedPolicy?.PolicyDetails?.PolicyHolder?.email);
      setValue(FORM_KEYS.MOBILE_NUMBER, initialSelectedPolicy?.PolicyDetails?.PolicyHolder?.phoneMobile);
      setSelectedPolicyDetails(initialSelectedPolicy);
    }
  }, [petDetails, petDetailsLoading, setValue, isLiteModeEnabled]);

  const handlePetSelection = (policyNumber) => {
    const selectedPet = petDetails.find((pet) => pet.FullPolicyNumber === policyNumber);
    const ageInDays = getAgeInDays(new Date(selectedPet.DateOfBirth));

    const selectedPetGender =
      selectedPet.PolicyDetails.PetGender === MALE_GENDER
        ? GENDER_VALUE.MALE_GENDER_VALUE
        : GENDER_VALUE.FEMALE_GENDER_VALUE;

    // Update form field values with selected pet details
    setValue(FORM_KEYS.FULL_POLICY_NUMBER, selectedPet.FullPolicyNumber);
    setValue(FORM_KEYS.PET_NAME, selectedPet.Name);
    setValue(FORM_KEYS.PET_SPECIES, selectedPet.Species);
    setValue(FORM_KEYS.PET_GENDER, selectedPetGender);
    setValue(FORM_KEYS.PET_BREED, selectedPet.Breed);
    setValue(FORM_KEYS.PET_AGE, GetAgeInYears({ ageInDays }) || 0);
    setSelectedPolicyDetails(selectedPet);
  };

  const onFormSubmit = async (formData) => {
    // Fire only on a valid submission (react-hook-form's handleSubmit gates this).
    // The auto-tracked click event can't carry the chat type because customData is static at
    // app start; this dedicated event carries it per submission. Deliberately "submitted", not
    // "started": generateConsultationUrl below can still fail, so a "started" event here would
    // count consultations that never began.
    getGtmService().pushVetConsultSubmitted(chatType);

    const policySource = !isLiteModeEnabled ? selectedPolicyDetails : null;
    const shouldUsePolicySource = !isLiteModeEnabled && !!policySource;
    const sourceAgeInDays = policySource?.DateOfBirth ? getAgeInDays(new Date(policySource.DateOfBirth)) : null;
    const sourcePetGender =
      policySource?.PolicyDetails?.PetGender === MALE_GENDER
        ? GENDER_VALUE.MALE_GENDER_VALUE
        : GENDER_VALUE.FEMALE_GENDER_VALUE;
    const sourcePetSpecies = policySource?.Species;
    const resolvedPetSpecies =
      (shouldUsePolicySource ? sourcePetSpecies ?? formData.petSpecies : formData.petSpecies) ?? '';

    const consultationRequest = {
      BrandPartnerCode: partnerPrefix,
      Policy: shouldUsePolicySource ? policySource.FullPolicyNumber : formData.fullPolicyNumber,
      ConsultType: chatType,
      petName: shouldUsePolicySource ? policySource.Name : formData.petName,
      petSpecies: resolvedPetSpecies.toUpperCase() === DOG_SPECIES_CODE ? DOG_SPECIES : CAT_SPECIES,
      petBreed: shouldUsePolicySource ? policySource.Breed : formData.petBreed,
      petAge: shouldUsePolicySource ? GetAgeInYears({ ageInDays: sourceAgeInDays }) || 0 : formData.petAge,
      petSex: shouldUsePolicySource ? sourcePetGender : formData.petGender,
      Query: formData.concernSummary,
      firstName:
        (shouldUsePolicySource ? policySource.PolicyDetails?.PolicyHolder?.firstName : undefined) ?? formData.firstName,
      lastName:
        (shouldUsePolicySource ? policySource.PolicyDetails?.PolicyHolder?.lastName : undefined) ?? formData.lastName,
      Email: formData.emailAddress,
      Mobile: formData.mobileNumber,
      AppModule: vetChatAppModule,
      ConsultationUrl: vetChatConsultationUrl,
      RedirectUrl: `${window.location.origin}${CSPUrlsMap.postConsultation.url}`
    };

    try {
      const consultationResponse = await generateConsultationUrl(consultationRequest);
      if (consultationResponse?.data?.isSuccessResponse) {
        const consultationUrl = consultationResponse?.data?.responseObject?.RefUrl;
        setConsultationUrl(consultationUrl);
        localStorage.setItem(SESSION_STORAGE_KEYS.LIVE_VET_SESSION, consultationUrl);
      } else {
        setFailedToStartConsultation(true);
      }
    } catch (error) {
      throw new Error('Error generating consultation URL:', error);
    }
  };

  return {
    petDetails,
    chatType,
    petGender,
    formContext,
    petDetailsLoading,
    failedToStartConsultation,
    fullPolicyNumber,
    isLiteModeEnabled,
    getValues,
    handleSubmit,
    onFormSubmit,
    handlePetSelection
  };
};
